import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bulkWriteIt, updateTileStatus } from "@/services/review.service";
import { BulkWriteItRequest } from "@/types/review.types";
import { ReviewTileItem } from "@/types/tile.types";
import { toast } from "sonner";
import {
  FailedTile,
  useBulkWriteItProgress,
} from "@/taskpane/components/BulkWriteItProgressContext";
import { useTileTextExtraction } from "./useTileTextExtraction";

interface BulkWriteItResult {
  successCount: number;
  failedCount: number;
  currentFailedTiles: FailedTile[];
}

interface TileStatusUpdates {
  [queryTerm: string]: number[];
}

function extractSuggestedClause(tile: ReviewTileItem): string | null {
  // Truthy checks (not `!== undefined`) so empty strings fall through to
  // `formatTileText`. Otherwise `getInsertText` would return "" and
  // `insertText("", ...replace)` would silently delete the matched clause
  // text from the legal document. Mirrors the guard in useTileInsert.ts.
  const originalData = tile.originalData;
  if (originalData == null) return null;
  if ("suggestedclause" in originalData && originalData.suggestedclause)
    return originalData.suggestedclause;
  if ("suggestion" in originalData && originalData.suggestion) return originalData.suggestion;
  if ("suggestions" in originalData && originalData.suggestions) return originalData.suggestions;
  if ("suggestedNumber" in originalData && originalData.suggestedNumber)
    return originalData.suggestedNumber;
  return null;
}

function formatTileText(tile: ReviewTileItem): string {
  let text = "";
  if (tile.clause != null) text += `**${tile.clause}**\n\n`;
  if (tile.reason != null) text += `**Reason:** ${tile.reason}\n\n`;
  if (tile.analysis != null) text += `**Analysis:** ${tile.analysis}\n\n`;
  if (tile.explanation != null) text += `**Explanation:** ${tile.explanation}\n\n`;
  return text.trim();
}

function getInsertText(item: ReviewTileItem): string {
  const suggestedClause = extractSuggestedClause(item);
  return suggestedClause ?? formatTileText(item);
}

function trackTileUpdate(updates: TileStatusUpdates, item: ReviewTileItem): void {
  if (item.query != null) {
    updates[item.query] ??= [];
    if (item.originalData?.tile_id !== undefined) {
      updates[item.query]?.push(item.originalData.tile_id);
    }
  }
}

async function flushTileStatusUpdates(
  updates: TileStatusUpdates,
  documentId: string,
  isCompleted: boolean
): Promise<void> {
  for (const queryTerm in updates) {
    const tileIds = updates[queryTerm];
    if (tileIds != null && tileIds.length > 0) {
      await updateTileStatus({
        document_id: [documentId],
        query_term: [queryTerm],
        tile_ids: tileIds,
        isCompleted,
        isFailed: !isCompleted,
      });
    }
  }
}

async function tryFallbackSearch(
  context: Word.RequestContext,
  truncatedText: string,
  searchOptions: Partial<Word.SearchOptions>,
  item: ReviewTileItem
): Promise<boolean> {
  const delimiters = [":", ";"];
  const searches: Word.RangeCollection[] = [];

  for (const delimiter of delimiters) {
    if (truncatedText.includes(delimiter)) {
      const firstPart = truncatedText.split(delimiter)[0]?.trim();
      if (firstPart != null && firstPart.length > 0) {
        const results = context.document.body.search(firstPart, searchOptions);
        context.load(results, "items");
        searches.push(results);
      }
    }
  }

  if (searches.length === 0) return false;

  await context.sync();

  let matchedRange: Word.Range | null = null;
  for (const results of searches) {
    if (results.items.length > 0 && results.items[0] != null) {
      matchedRange = results.items[0];
      break;
    }
  }

  if (matchedRange != null) {
    matchedRange.insertText(getInsertText(item), Word.InsertLocation.replace);
    await context.sync();
    return true;
  }

  return false;
}

async function processSingleTile(
  item: ReviewTileItem,
  getSearchableText: (tile: ReviewTileItem) => string,
  normalizeSearchText: (text: string) => string
): Promise<void> {
  await Word.run(async (context) => {
    const searchableText = getSearchableText(item);
    const normalizedText = normalizeSearchText(searchableText);
    if (normalizedText === "") throw new Error("No searchable text found for this tile");

    const truncatedText =
      normalizedText.length > 255 ? normalizedText.substring(0, 255) : normalizedText;
    const searchOptions = {
      ignorePunct: true,
      ignoreSpace: true,
      matchCase: false,
      matchWholeWord: false,
    };
    const searchResults = context.document.body.search(truncatedText, searchOptions);
    context.load(searchResults, "items");
    await context.sync();

    if (searchResults.items.length === 0) {
      const found = await tryFallbackSearch(context, truncatedText, searchOptions, item);
      if (!found) throw new Error("Original text not found in document");
    } else if (searchResults.items[0] != null) {
      searchResults.items[0].insertText(getInsertText(item), Word.InsertLocation.replace);
      await context.sync();
    }
  });
}

async function performClientSideInsertion(
  items: ReviewTileItem[],
  documentId: string,
  getSearchableText: (tile: ReviewTileItem) => string,
  normalizeSearchText: (text: string) => string,
  updateProgressFn: (progress: number, message: string, failedTiles: FailedTile[]) => void
): Promise<BulkWriteItResult> {
  let successCount = 0;
  let failedCount = 0;
  const currentFailedTiles: FailedTile[] = [];
  const successfulUpdates: TileStatusUpdates = {};
  const failedUpdates: TileStatusUpdates = {};

  for (const [index, item] of items.entries()) {
    try {
      await processSingleTile(item, getSearchableText, normalizeSearchText);
      successCount++;
      trackTileUpdate(successfulUpdates, item);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      failedCount++;
      currentFailedTiles.push({
        id: item.id,
        text: (item.clause ?? item.id) || "Unknown Tile",
        reason: message,
      });
      trackTileUpdate(failedUpdates, item);
    }
    updateProgressFn(
      ((index + 1) / items.length) * 100,
      `Processing tile ${index + 1} of ${items.length}...`,
      currentFailedTiles
    );
  }

  await flushTileStatusUpdates(successfulUpdates, documentId, true);
  await flushTileStatusUpdates(failedUpdates, documentId, false);

  return { successCount, failedCount, currentFailedTiles };
}

export const useBulkWriteIt = () => {
  const queryClient = useQueryClient();
  const { updateProgress, completeProgress, resetProgress } = useBulkWriteItProgress();
  const { getSearchableText, normalizeSearchText } = useTileTextExtraction();

  return useMutation({
    mutationFn: async ({ items, documentId }: { items: ReviewTileItem[]; documentId: string }) => {
      resetProgress();

      const bulkWriteItRequest: BulkWriteItRequest = {
        document_id: [documentId],
        tile_ids: items.map((item) => String(item.originalData?.tile_id)),
        query_term: items
          .map((item) => item.query)
          .filter((query): query is string => query !== undefined),
      };

      bulkWriteIt(bulkWriteItRequest).catch((err: unknown) =>
        console.error("Background bulk write API failed:", err)
      );

      return await performClientSideInsertion(
        items,
        documentId,
        getSearchableText,
        normalizeSearchText,
        updateProgress
      );
    },
    onSuccess: ({ successCount, failedCount, currentFailedTiles }) => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      completeProgress(currentFailedTiles);
      if (failedCount > 0) {
        toast.error(
          `Bulk write completed with issues: ${successCount} successful, ${failedCount} failed.`
        );
      } else {
        toast.success(`Bulk write completed: ${successCount} successful.`);
      }
    },
    onError: (error: Error) => {
      console.error("Bulk write operation failed:", error);
      toast.error("Bulk write operation failed", { description: error.message });
      completeProgress([]);
    },
  });
};
